import React from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { FormField } from '../../FormField/FormField';
import { AdaptiveSelect } from '../../AdaptiveSelect/AdaptiveSelect';
import { LOCALES } from '../../../config/locales';
import { ValidationErrors } from '../../../hooks/useForm';
import { RoutineExercise } from '../../../types/Fisiofit/routine';
import { ExerciseShort } from '../../../types/Fisiofit/exercise';
import { MUSCLE_GROUPS, EXERCISE_CATEGORIES } from '../../../types/Fisiofit/fisiofitConst';
import { getExerciseOptions } from '../../../utils/selectOptions';
import { RoutineForm } from '../validators';
import { RoutineExercisesActions } from '../types';

interface SingleExerciseFieldsProps {
  singleExercise: RoutineExercise;
  dayIndex: number;
  exerciseIndex: number;
  isDuplicate: boolean;
  shouldDisableInputs: boolean;
  isRepetitionExercise: boolean;
  validationErrors: ValidationErrors<RoutineForm> | undefined;
  locale: LOCALES;
  organizationExercises: ExerciseShort[] | undefined;
  selectedExerciseMuscleGroups: MUSCLE_GROUPS[];
  selectedExerciseCategories: EXERCISE_CATEGORIES[];
  isLoadingExercisePreloadValues: boolean;
  exercises: RoutineExercisesActions;
}

export const SingleExerciseFields = ({
  singleExercise,
  dayIndex,
  exerciseIndex,
  isDuplicate,
  shouldDisableInputs,
  isRepetitionExercise,
  validationErrors,
  locale,
  organizationExercises,
  selectedExerciseMuscleGroups,
  selectedExerciseCategories,
  isLoadingExercisePreloadValues,
  exercises,
}: SingleExerciseFieldsProps) => {
  return (
    <div className='exercise-form-fields'>
      {isDuplicate && (
        <div className='duplicate-warning'>
          <i className='pi pi-exclamation-triangle' style={{ color: 'var(--warning)', marginRight: 4 }} />
          Este ejercicio está duplicado en este día. No puede tener valores diferentes en el mismo día.
        </div>
      )}
      <FormField
        labelTitle='Ejercicio'
        smallLabel
        fullWidth
        error={validationErrors?.exerciseDays && !singleExercise.exerciseId ? 'Debes elegir un ejercicio' : undefined}
        dontShowErrorMessage
      >
        <AdaptiveSelect
          value={singleExercise.exerciseId}
          options={
            organizationExercises
              ? getExerciseOptions(organizationExercises, selectedExerciseMuscleGroups, selectedExerciseCategories)
              : []
          }
          onChange={(value: string) => {
            exercises.handleExerciseSelect(dayIndex, exerciseIndex, value);
          }}
          placeholder='Selecciona un ejercicio'
          modalTitle='Seleccionar ejercicio'
          searchPlaceholder='Buscar ejercicio'
          emptyMessage='No hay ejercicios'
          emptyFilterMessage='No se han encontrado ejercicios'
          itemTemplate={(option) => <span>{option.label}</span>}
          invalid={!!validationErrors?.exerciseDays && !singleExercise.exerciseId}
          loading={isLoadingExercisePreloadValues}
        />
      </FormField>

      {singleExercise.exerciseId && (
        <>
          <FormField className='exercise-field' labelTitle='Series' smallLabel>
            <InputNumber
              value={singleExercise.sets}
              onChange={(e) => {
                if (!shouldDisableInputs) {
                  e.originalEvent?.stopPropagation();
                  exercises.updateExercise(dayIndex, exerciseIndex, { sets: e.value ?? undefined });
                }
              }}
              min={1}
              max={100}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={0}
              onClick={(e) => e.stopPropagation()}
              disabled={shouldDisableInputs}
            />
          </FormField>

          {isRepetitionExercise ? (
            <FormField className='exercise-field' labelTitle='Reps' smallLabel>
              <InputNumber
                value={'repetitions' in singleExercise ? singleExercise.repetitions : undefined}
                onChange={(e) => {
                  if (!shouldDisableInputs) {
                    e.originalEvent?.stopPropagation();
                    exercises.updateExercise(dayIndex, exerciseIndex, { repetitions: e.value ?? undefined });
                  }
                }}
                min={1}
                max={1000}
                useGrouping={false}
                locale={locale === 'es' ? 'es-ES' : 'en-US'}
                minFractionDigits={0}
                maxFractionDigits={0}
                onClick={(e) => e.stopPropagation()}
                disabled={shouldDisableInputs}
              />
            </FormField>
          ) : (
            <FormField className='exercise-field' labelTitle='Tiempo (s)' smallLabel>
              <InputNumber
                value={'duration' in singleExercise ? singleExercise.duration : undefined}
                onChange={(e) => {
                  if (!shouldDisableInputs) {
                    e.originalEvent?.stopPropagation();
                    exercises.updateExercise(dayIndex, exerciseIndex, { duration: e.value ?? undefined });
                  }
                }}
                min={1}
                max={3600}
                useGrouping={false}
                locale={locale === 'es' ? 'es-ES' : 'en-US'}
                minFractionDigits={0}
                maxFractionDigits={0}
                onClick={(e) => e.stopPropagation()}
                disabled={shouldDisableInputs}
              />
            </FormField>
          )}

          <FormField className='exercise-field' labelTitle='Peso (kg)' smallLabel>
            <InputNumber
              value={singleExercise.weight ?? null}
              onChange={(e) => {
                if (!shouldDisableInputs) {
                  e.originalEvent?.stopPropagation();
                  exercises.updateExercise(dayIndex, exerciseIndex, { weight: e.value ?? undefined });
                }
              }}
              min={0}
              max={999}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={2}
              onClick={(e) => e.stopPropagation()}
              disabled={shouldDisableInputs}
            />
          </FormField>

          <FormField className='exercise-field' labelTitle='RIR' smallLabel>
            <InputNumber
              value={singleExercise.rir}
              onChange={(e) => {
                if (!shouldDisableInputs) {
                  e.originalEvent?.stopPropagation();
                  exercises.updateExercise(dayIndex, exerciseIndex, { rir: e.value ?? undefined });
                }
              }}
              min={0}
              max={10}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={0}
              onClick={(e) => e.stopPropagation()}
              disabled={shouldDisableInputs}
            />
          </FormField>

          <FormField className='exercise-field' labelTitle='RPE' smallLabel>
            <InputNumber
              value={singleExercise.rpe}
              onChange={(e) => {
                if (!shouldDisableInputs) {
                  e.originalEvent?.stopPropagation();
                  exercises.updateExercise(dayIndex, exerciseIndex, { rpe: e.value ?? undefined });
                }
              }}
              min={1}
              max={10}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={0}
              onClick={(e) => e.stopPropagation()}
              disabled={shouldDisableInputs}
            />
          </FormField>

          <FormField className='exercise-field comment-field' labelTitle='Comentario' smallLabel fullWidth>
            <InputTextarea
              value={singleExercise.comment ?? ''}
              onChange={(e) => {
                e.stopPropagation();
                exercises.updateExercise(dayIndex, exerciseIndex, { comment: e.target.value || undefined });
              }}
              placeholder='Comentario'
              rows={3}
              onClick={(e) => e.stopPropagation()}
            />
          </FormField>
        </>
      )}
    </div>
  );
};
